import React from 'react';
import { ModalBody } from '../../../../../../../CommonComponents/OPL/Modal/ModalBody';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency, formatPercentage } from '../../../../../../../CommonUtils/formatNumber';
import { AssetsTable } from '../AssetsTable/AssetsTable';
import { BucketData } from '../../../useAssetBucketingResults';
import { Dot } from '../../../../../../../CommonComponents/OPL/Dot/Dot';
import { PieChart } from '../../../../../../../CommonComponents/OPL/charts/PieChart/PieChart';
import { EmptyState } from '../../../../../../../CommonComponents/OPL/AsyncStates/EmptyState/EmptyState';
import { Tooltip } from '../../../../../../../CommonComponents/OPL/Tooltip/Tooltip';

interface BucketTabProps {
  data: BucketData;
}

export const BucketTab: React.FC<BucketTabProps> = ({ data }) => {
  return (
    <>
      <ModalBody>
        <Box display="flex" justifyContent="center" mb="8">
          <Box width={216} height={216}>
            {data.total === 0 ? (
              <EmptyState variant="chart" />
            ) : (
              <PieChart
                series={[{ data: data.data.map(d => ({ ...d, y: Math.abs(d.y) })) }]}
                customSettings={{
                  plotOptions: {
                    pie: {
                      innerSize: '75%',
                      borderWidth: 1,
                    },
                  },
                }}
              />
            )}
          </Box>
        </Box>
      </ModalBody>

      <AssetsTable minHeight={350}>
        <tr>
          <Box as="td" display="flex" alignItems="center">
            <Text fontWeight={600}>{data.title}</Text>
          </Box>

          <Text as="td" fontWeight={600} variant="tabular" textAlign="right">
            {data.total !== 0 ? formatPercentage(1, { decimalPlaces: 2 }) : ''}
          </Text>

          <Text as="td" fontWeight={600} variant="tabular" textAlign="right">
            {formatCurrency(data.total)}
          </Text>
        </tr>

        {data.data.map(({ name, y, color, assets, displayValue }) => {
          if (!assets) {
            return null;
          }

          const isPercentageZero = data.total && Math.abs(y / data.total) === 0;

          return (
            <React.Fragment key={name}>
              <tr>
                <td colSpan={assets.length !== 1 ? 1 : 3}>
                  <Box display="flex" alignItems="center">
                    <Dot size={8} bg={color} mr="4" />
                    <Text fontWeight={500} color="neutral.500">
                      {name}
                    </Text>
                  </Box>
                </td>

                {assets.length !== 1 && (
                  <>
                    {data.total !== 0 && (
                      <Text
                        as="td"
                        fontWeight={500}
                        color="neutral.500"
                        variant="tabular"
                        textAlign={isPercentageZero ? 'center' : 'right'}
                      >
                        {isPercentageZero
                          ? '-'
                          : formatPercentage(Math.abs(y / data.total), { decimalPlaces: 2 })}
                      </Text>
                    )}

                    <Text
                      as="td"
                      fontWeight={500}
                      color="neutral.500"
                      variant="tabular"
                      textAlign={displayValue === null ? 'center' : 'right'}
                    >
                      {displayValue === null ? '-' : formatCurrency(displayValue)}
                    </Text>
                  </>
                )}
              </tr>

              {assets.map(asset => {
                const isAssetPercentageZero =
                  data.total && Math.abs(asset.networthValue / data.total) === 0;

                return (
                  <tr key={asset.name}>
                    <td style={{ paddingLeft: 32 }}>
                      <Tooltip content={asset.name} isOverflowWrap>
                        <Text
                          fontWeight={500}
                          css={`
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                          `}
                        >
                          {asset.name}
                        </Text>
                      </Tooltip>

                      {asset.institutionName && (
                        <Text fontSize="small" color="neutral.600">
                          {asset.institutionName}
                        </Text>
                      )}
                    </td>

                    {data.total && (
                      <Text
                        as="td"
                        fontWeight={500}
                        color="neutral.500"
                        variant="tabular"
                        textAlign={isAssetPercentageZero ? 'center' : 'right'}
                      >
                        {isAssetPercentageZero
                          ? '-'
                          : formatPercentage(Math.abs(asset.networthValue / data.total), {
                              decimalPlaces: 2,
                            })}
                      </Text>
                    )}

                    <Text
                      as="td"
                      fontWeight={600}
                      color="neutral.500"
                      variant="tabular"
                      textAlign={asset.displayValue === null ? 'center' : 'right'}
                    >
                      {asset.displayValue === null ? '-' : formatCurrency(asset.displayValue)}
                    </Text>
                  </tr>
                );
              })}
            </React.Fragment>
          );
        })}
      </AssetsTable>
    </>
  );
};
